import { UnavailabilityContent } from '@/components/Forms/ServiceUnAvailable';

import { Reason } from '@/contexts/ServiceAvailability/ServiceAvailabilityContext';

import { ERZServiceLockedReason, FeatureName, ResponseFeatures } from '@/api/types';

export const getERZReasonedMessage = (
  reason: ResponseFeatures[FeatureName.ERZ_SERVICES][1],
): UnavailabilityContent => ({
  title: 'На жаль, послуга не доступна.',
  message: (
    <>
      {reason === Reason.REGISTERED_ADDRESS_ABROAD && getRegisteredAddressAbroadGeneralMessage().message}
      {reason === ERZServiceLockedReason.TIMEOUT_PERMIT && (
        <div>
          У Вас в наявності є зброя без дозволу на зберігання, носіння вогнепальної мисливської зброї, основних частин
          зброї, пневматичної, холодної, охолощеної зброї, пристроїв. Вам необхідно звернутися до відповідного органу
          НПУ за місцем зберігання Вашої зброї.
        </div>
      )}
      {reason === ERZServiceLockedReason.EXPIRED_PERMIT_TEMP_RELOCATION && (
        <div>
          Вами протермінований строк подання заяви, необхідної для повернення з тимчасової зміни місця зберігання зброї,
          основних частин зброї, пристроїв.
        </div>
      )}
      {reason === ERZServiceLockedReason.EXPIRED_PERMIT_STORAGE_AND_STORE && (
        <div>
          У Вас є в наявності протермінований дозвіл на зберігання, носіння вогнепальної мисливської зброї, основних
          частин зброї, пневматичної, холодної, охолощеної зброї, пристроїв. Вам необхідно звернутися до відповідного
          органу НПУ за місцем зберігання Вашої зброї.
        </div>
      )}
      {reason === ERZServiceLockedReason.NO_CITIZENSHIP_OR_RESIDENCE_DOCUMENT && (
        <div>
          Відповідно до п. 12.1 Інструкції, затвердженої наказом МВС від 21.08.1998 № 622 послуги для замовлення
          дозвільних документів, недоступні
        </div>
      )}
    </>
  ),
});

export const getCommonMsg = (): UnavailabilityContent => ({
  title: 'На жаль, в реєстрах недостатньо інформації для оформлення онлайн послуги.',
  message: (
    <div>
      <p>Для отримання послуги Ви можете звернутися:</p>
      <ul>
        <li>особисто або уповноваженою особою (представником) до органу (підрозділу) поліції.</li>
        <li>
          особисто через суб’єкта господарювання, який має ліцензію на провадження господарської діяльності з торгівлі
          вогнепальною зброєю невійськового призначення та боєприпасами до неї, холодною зброєю, пневматичною зброєю
          калібру понад 4,5 міліметра і швидкістю польоту кулі понад 100 метрів на секунду, незалежно від місця
          проживання.
        </li>
      </ul>
    </div>
  ),
});

export const getTRRMsg = (
  reason?: ResponseFeatures[FeatureName.ERZ_TEMPORARY_RESIDENCE_RELOCATION][1],
): UnavailabilityContent => {
  return reason
    ? {
        title: 'На жаль, послуга не доступна.',
        message: (
          <div>
            Ви вже подали заяву на тимчасову зміну місця зберігання зброї, основних частин зброї, пристроїв. Вам
            необхідно дочекатися опрацювання заяви працівниками органу Національної поліції України.
          </div>
        ),
      }
    : {
        title: 'Зверніть увагу!',
        message: (
          <div>
            <p>Ви вже маєте дозвіл на тимчасову зміну місця зберігання зброї, основних частин зброї, пристроїв.</p>
            <p>Скасуйте його для отримання нового дозволу.</p>
          </div>
        ),
      };
};

export const getTRRCancelationMsg = (
  reason: ResponseFeatures[FeatureName.ERZ_CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION][1],
): UnavailabilityContent => {
  return reason
    ? {
        title: 'На жаль, послуга не доступна.',
        message: (
          <div>
            Ви вже подали заяву на повернення після тимчасової зміни місця зберігання зброї, основних частин зброї,
            пристроїв. Вам необхідно дочекатися опрацювання заяви працівниками органу Національної поліції України.
          </div>
        ),
      }
    : {
        title: 'Зверніть увагу!',
        message: (
          <p>Ви не маєте дозволу на тимчасову зміну місця зберігання зброї, основних частин зброї, пристроїв.</p>
        ),
      };
};

export const getIACReasonedMessage = (reason: ResponseFeatures[FeatureName.IAC_SERVICES][1]): UnavailabilityContent => {
  if (reason === Reason.MIN_USER_AGE) {
    return {
      title: 'Послуги доступні користувачам з 14 років',
      message: (
        <div>
          Якщо ви вказали помилкову дату, ви можете виправити її в особистому кабінеті.
          <br />
          Наголошуємо, що в разі вказання фіктивної дати народження, ви не отримаєте витяг.
        </div>
      ),
    };
  }

  if (reason === Reason.UNAVAILABLE_MIN_USER_AGE) {
    return {
      title: 'Немає доступу до послуги',
      message: <div>Для перевірки доступності послуги, будь ласка, вкажіть дату народження в особистому кабінеті.</div>,
    };
  }

  return getUnhandledUnavailabilityMessage();
};

export const getBIAReasonedMessage = (reason: ResponseFeatures[FeatureName.BIA_SERVICES][1]): UnavailabilityContent => {
  if (reason === Reason.MIN_USER_AGE) {
    return {
      message: 'Послуги доступні користувачам з 18 років',
    };
  }

  if (reason === Reason.SERVICE_NOT_AVAILABLE) {
    return getServiceUnAvailableMessage();
  }

  if (reason === Reason.RNOKPP_MISSING) {
    return {
      title: 'Послуга не доступна',
      message: (
        <div>
          Ви не маєте власного РНОКПП (ідентифікаційний код).Для отримання послуги страхування громадянин мусить мати
          РНОКПП (ідентифікаційний код).
        </div>
      ),
    };
  }

  return getUnhandledUnavailabilityMessage();
};

export const getServiceUnAvailableMessage = () => ({
  title: 'Сервіс наразі недоступний',
  message: 'Спробуйте, будь ласка, пізніше',
});

export const getRegisteredAddressAbroadGeneralMessage = (): UnavailabilityContent => ({
  message: (
    <div>
      Відповідно до п. 12.1 Інструкції, затвердженої наказом МВС від 21.08.1998 № 622 послуги для замовлення дозвільних
      документів, недоступні
    </div>
  ),
});

const getUnhandledUnavailabilityMessage = (): UnavailabilityContent => ({
  title: 'Помилка',
  message: 'Сталася помилка',
});

export { MinUserAgeMessage } from './MinUserAgeMessage';
